import React, { useState } from 'react'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

import { db } from '../../FirebaseInit'
import { useToast } from '../../context/ToastContext'

// ReplyComposer (design: Pages and UI components; Req 4.1, 4.2, 4.4, 5.1, 6.4)
//
// Appends a message to a ticket's `messages` subcollection. The composer is
// disabled entirely when the ticket is Closed (Req 6.4 / 4.3 — UI gate; rules
// are authoritative). The internal-comment toggle is only rendered for admins,
// so a customer can never flag a message internal (Req 5.1). Submitted messages
// record the author's email, role, internal flag, and a creation timestamp
// (Req 4.4).
const ReplyComposer = ({ ticketId, status, admin, authorEmail }) => {
  const { showToast } = useToast()
  const [body, setBody] = useState('')
  const [internal, setInternal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const isClosed = status === 'Closed'

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isClosed) return

    const trimmed = body.trim()
    if (trimmed === '') return

    setSubmitting(true)
    try {
      await addDoc(collection(db, 'tickets', ticketId, 'messages'), {
        authorEmail,
        authorRole: admin ? 'admin' : 'customer',
        internal: admin ? internal : false,
        body: trimmed,
        attachments: [],
        createdAt: serverTimestamp()
      })
      setBody('')
      setInternal(false)
      showToast('Reply sent', 'green')
    } catch (err) {
      console.error('Failed to send reply:', err)
      showToast('Failed to send reply', 'red')
    } finally {
      setSubmitting(false)
    }
  }

  if (isClosed) {
    return (
      <p className='p-3 m-2 text-sm text-gray-500'>
        This ticket is closed. No further replies can be added.
      </p>
    )
  }

  return (
    <form onSubmit={handleSubmit} className='p-3 m-2'>
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder='Write a reply...'
        rows={3}
        disabled={submitting}
        className='block w-full rounded border border-gray-300 p-2 text-sm'
      />

      {admin && (
        <label className='mt-2 flex items-center gap-2 text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={internal}
            onChange={(event) => setInternal(event.target.checked)}
            disabled={submitting}
          />
          Internal comment (visible to admins only)
        </label>
      )}

      <button
        type='submit'
        disabled={submitting || body.trim() === ''}
        className='mt-2 rounded bg-blue-600 px-4 py-1 text-sm text-white hover:bg-blue-700 disabled:bg-gray-300'
      >
        {internal ? 'Add internal comment' : 'Send reply'}
      </button>
    </form>
  )
}

export default ReplyComposer
